import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import { BooleanTags } from "../../components/common/registerUtils";
import { IFormSection, TFormHookParams } from "../../types/common";
import {
  createColumn,
  createEditAction,
  createViewAction,
  getForm,
  getRegister,
} from "../../utils/helpers";
import { URL_SERVICE_TYPES } from "../../utils/urls";
import useGet from "../common/useGet";
import { API_DISTRICTS } from "../../utils/apis";

export const useServiceTypes = () => {
  const tableColumns = () => [
    createColumn("name", true, "Name"),
    createColumn("city", true, "City"),
    createColumn("active", false, "Active"),
    createColumn("car_type_dependant", false, "Car Type Dependant"),
    createColumn("vehicle_type_dependant", false, "Vehicle Type Dependant"),
    createColumn("car_size_dependant", false, "Car Size Dependant"),
    createColumn("actions", false),
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => {
      return {
        ...item,
        active: <BooleanTags status={item?.active} />,
        car_type_dependant: <BooleanTags status={item?.car_type_dependant} />,
        vehicle_type_dependant: (
          <BooleanTags status={item?.vehicle_type_dependant} />
        ),
        car_size_dependant: <BooleanTags status={item?.car_size_dependant} />,
        city: item?.city_details?.map((city: any) => city.name).join(", "),
        actions: (
          <FormActions
            actions={[
              createViewAction(URL_SERVICE_TYPES, item?.id),
              createEditAction(URL_SERVICE_TYPES, item?.id),
            ]}
          />
        ),
      };
    });
  };

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }
    return {
      sections: [
        {
          title: "Service Type Details",
          fields: {
            name: item?.name,
            city: item?.city,
            active: item?.active,
            car_type_dependant: item?.car_type_dependant,
            vehicle_type_dependant: item?.vehicle_type_dependant,
            car_size_dependant: item?.car_size_dependant,
          },
        },
      ],
    };
  };

  const formRoute = getForm(URL_SERVICE_TYPES);

  return { tableColumns, tableData, viewData, formRoute };
};

export const useServiceTypesForm = ({
  updateFormValues,
}: TFormHookParams = {}) => {
  const {
    loadData: loadDistricts,
    loading: loadingDistricts,
    data: districts,
  } = useGet();

  useEffect(() => {
    loadDistricts({ api: API_DISTRICTS });
  }, []);

  useEffect(() => {
    if (updateFormValues) {
      updateFormValues();
    }
  }, [updateFormValues]);

  const sections: IFormSection[] = [
    {
      title: "Service Type Details",
      fields: [
        {
          field_name: "name",
          label: "Name",
          type: "text",
          props: { required: true, placeholder: "Enter name" },
        },
        {
          field_name: "city",
          label: "City",
          type: "select",
          props: {
            required: true,
            placeholder: "Select City",
            multiple: true,
            options:
              districts?.map((district: any) => ({
                value: district.id,
                label: district.name,
              })) || [],
          },
        },
        {
          field_name: "active",
          label: "Active",
          type: "select",
          props: {
            required: true,
            options: [
              { value: true, label: "Active" },
              { value: false, label: "Inactive" },
            ],
          },
        },
        {
          field_name: "car_type_dependant",
          label: "Car Type Dependant",
          type: "select",
          props: {
            required: true,
            options: [
              { value: true, label: "Active" },
              { value: false, label: "Inactive" },
            ],
          },
        },
        {
          field_name: "vehicle_type_dependant",
          label: "Vehicle Type Dependant",
          type: "select",
          props: {
            required: true,
            options: [
              { value: true, label: "Active" },
              { value: false, label: "Inactive" },
            ],
          },
        },
        {
          field_name: "car_size_dependant",
          label: "Car Size Dependant",
          type: "select",
          props: {
            required: true,
            options: [
              { value: true, label: "Active" },
              { value: false, label: "Inactive" },
            ],
          },
        },
      ],
    },
  ];

  const title = "Service Types";
  const redirect = getRegister(URL_SERVICE_TYPES);

  return { sections, loadingDistricts, districts, title, redirect };
};
